/**
 * Global element styles for the app — resets, base typography, headings/lists,
 * link styles, Fluent UI global overrides, and print rules. Defined in TS
 * (Griffel makeStaticStyles) so colors draw from the single source of truth in
 * styles/colors.ts.
 *
 * These selectors live outside the FluentProvider scope and so can't use Fluent
 * theme CSS vars; makeStaticStyles injects them as plain global rules. The body
 * background is also kept in sync with the active theme at runtime in App.tsx.
 *
 * Passed as a raw CSS string (makeStaticStyles supports it via compileCSSRules)
 * so nested at-rules (@media) and pseudo-element selectors emit faithfully —
 * the object form can't express those. Call useGlobalStyles() once at the app root.
 */
import { makeStaticStyles } from '@fluentui/react-components';
import { brand, overlay } from './colors';

export const useGlobalStyles = makeStaticStyles(`
  /* ── Box-model reset ─────────────────────────────────────────────────── */
  *, *::before, *::after { box-sizing: border-box; }

  /* Always show the vertical scrollbar track so short pages (no scrollbar)
     don't render ~15px wider than scrolling pages — this keeps the layout from
     shifting horizontally when navigating between them. Unlike
     scrollbar-gutter, a real persistent scrollbar leaves no empty strip beside
     the fixed header. */
  html { font-size: 16px; -webkit-text-size-adjust: 100%; overflow-y: scroll; }

  body {
    margin: 0;
    padding: 0;
    min-height: 100vh;
    background-color: ${brand.background};
    color: ${brand.bodyText};
    font-family: ${brand.fontFamily};
    font-size: 0.875rem;
    font-weight: 300;
    line-height: 1.55;
    letter-spacing: 0.025em;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* ── App shell ───────────────────────────────────────────────────────── */
  #root { min-height: 100vh; display: flex; flex-direction: column; }

  /*
   * App-shell layout for the root FluentProvider element. Scoped to the #root
   * child so it does NOT reach popup portal nodes — Fluent copies any className
   * passed to <FluentProvider> onto every portal (Dropdown/Menu/Dialog), which
   * would turn them into full-viewport overlays that hide popups & block clicks.
   * (ADR-3, stack-conventions)
   */
  #root > .fui-FluentProvider {
    min-height: 100vh;
    background: transparent;
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  /* ── Element resets ──────────────────────────────────────────────────── */
  button {
    font-family: inherit;
    font-size: inherit;
    cursor: pointer;
    border: none;
    background: none;
    padding: 0;
  }

  input, textarea, select { font-family: inherit; font-size: inherit; }

  img { max-width: 100%; height: auto; }

  /* ── Headings ────────────────────────────────────────────────────────── */
  /* color: inherit so headings follow the active Fluent theme; hardcoding
     #1A1A1A breaks dark mode. */
  h1, h2, h3, h4, h5, h6 {
    margin-top: 0;
    margin-bottom: 1rem;
    font-family: ${brand.fontFamily};
    font-weight: 500;
    line-height: 1.2;
    color: inherit;
  }
  h1 { font-size: 2rem;      text-transform: uppercase; letter-spacing: 0.1em; }
  h2 { font-size: 1.5rem;    text-transform: uppercase; letter-spacing: 0.06em; }
  h3 { font-size: 1.25rem;   text-transform: uppercase; letter-spacing: 0.06em; }
  h4 { font-size: 1rem;      letter-spacing: 0.02em; }
  h5 { font-size: 0.875rem;  letter-spacing: 0.02em; }
  h6 { font-size: 0.75rem;   letter-spacing: 0.02em; }

  /* ── Links ───────────────────────────────────────────────────────────── */
  a { color: ${brand.red}; text-decoration: none; transition: color 150ms ease-out; }
  a:hover { color: ${brand.redDark}; }
  a:focus-visible { outline: 2px solid ${brand.red}; outline-offset: 2px; }

  /* ── Lists ───────────────────────────────────────────────────────────── */
  ul, ol { padding-left: 1.5rem; margin-bottom: 1rem; }
  ul li, ol li { margin-bottom: 0.25rem; }

  /* ── Fluent UI v9 global overrides ──────────────────────────────────── */

  /* Uppercase table headers across all Fluent tables (brand treatment) */
  .fui-TableHeaderCell { text-transform: uppercase; font-size: 11px; letter-spacing: 0.05em; }

  /* Dropdown/Combobox popups: size to the widest option instead of the trigger
     width so long option labels don't wrap. Doubled class selectors beat
     Griffel's single-class atomic styles on specificity. */
  .fui-Listbox.fui-Listbox { min-width: max-content; max-width: min(420px, 90vw); }

  /* Dropdown/Combobox triggers: Fluent forces min-width 250px, which oversizes
     filters with short labels. Form fields are unaffected — Field stretches its
     control to full width regardless of min-width. */
  .fui-Dropdown.fui-Dropdown, .fui-Combobox.fui-Combobox { min-width: 150px; }

  /* Establishes an inline-size container so children can use @container queries
     to switch between card and table views based on available width. */
  .table-card-container { container-type: inline-size; }

  /* Table scroll containers: always show a horizontal scrollbar on WebKit so
     users can discover scrollable content without hovering first. (macOS hides
     scrollbars by default; this overrides that for these wrappers.) */
  .table-scroll-x { overflow-x: auto; }
  .table-scroll-x::-webkit-scrollbar { height: 6px; }
  .table-scroll-x::-webkit-scrollbar-track { background: transparent; }
  .table-scroll-x::-webkit-scrollbar-thumb { background: ${overlay.scrollThumb}; border-radius: 3px; }

  /* ── Print ───────────────────────────────────────────────────────────── */
  @media print {
    body { background: ${brand.white}; }
    a { color: inherit; }
  }
`);
